import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  FormHelperText,
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Alert,
  type SelectChangeEvent,
} from "@mui/material"
import api from "../../services/api"
import type { Breed } from "@/src/interfaces"
import HorseLoader from "../../components/loadingHorse"
import { useIsMobile, useIsTablet } from "../../hooks/useMediaQuery"
import { useCryptoNavigation, useAppNavigation } from "../../App"

interface HorseFormData {
  horseId?: number
  horseName: string
  horseGender: string
  horseBirth: string
  horseBreed: string
  horseBreedName?: string
  horseFather: string
  horseMother: string
  horseCastrated: boolean
  horsePregnant?: boolean
  horseChildBirth?: string
  horseChildNumber?: string
}

interface FormErrors {
  horseName?: string
  horseGender?: string
  horseBreed?: string
  [key: string]: string | undefined
}

// Normaliza uma data vinda da API para o formato aceito pelo <input type="date">
// (yyyy-MM-dd). Cobre ISO com "T", datetime com espaço ("yyyy-MM-dd HH:mm:ss")
// e o formato pt-BR "dd/MM/yyyy". Sem isso, campos que não estão exatamente em
// yyyy-MM-dd não pré-preenchem ao editar.
const toDateInput = (value: any): string => {
  if (!value) return ""
  const raw = String(value).split("T")[0].split(" ")[0]
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw
  const m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (m) return `${m[3]}-${m[2]}-${m[1]}`
  return ""
}

export default function HorseForm() {
  const { t } = useTranslation()
  const { navigateToPage } = useCryptoNavigation()
  const { getParam } = useAppNavigation()
  
  const id = getParam("id")
  const mode = getParam("mode")
  const tenantId = getParam("tenantId") || "1"

  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const isEditMode = mode === "edit" && Boolean(id)

  const [horseData, setHorseData] = useState<HorseFormData>({
    horseName: "",
    horseGender: "",
    horseBirth: "",
    horseBreed: "",
    horseFather: "",
    horseMother: "",
    horseCastrated: false,
    horsePregnant: false,
    horseChildBirth: "",
    horseChildNumber: "",
  })

  const [originalData, setOriginalData] = useState<HorseFormData>({ ...horseData })
  const [modifiedFields, setModifiedFields] = useState<Set<string>>(new Set())

  const [breeds, setBreeds] = useState<Breed[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [noChangesWarning, setNoChangesWarning] = useState(false)

  // Carrega dados do cavalo para edição
  useEffect(() => {
    if (!isEditMode) return setLoading(false)

    setLoading(true)
    api.get(`/horse/${tenantId}/${id}`)
      .then(res => {
        const data = res.data.horse || res.data
        
        const formatted: HorseFormData = {
          horseId: data.horseId,
          horseName: data.horseName || "",
          horseGender: data.horseGender || "",
          horseBirth: toDateInput(data.horseBirth),
          horseBreed: data.horseBreed ? String(data.horseBreed) : "",
          horseBreedName: data.horseBreedName || "",
          horseFather: data.horseFather || "",
          horseMother: data.horseMother || "",
          horseCastrated: !!data.horseCastrated,
          horsePregnant: data.horseGender === "F" ? !!data.horsePregnant : undefined,
          horseChildBirth: toDateInput(data.horseChildBirth),
          horseChildNumber: data.horseChildNumber ? String(data.horseChildNumber) : "",
        }
        
        console.log("📥 Dados carregados:", formatted)
        
        setHorseData(formatted)
        setOriginalData(formatted)
      })
      .catch(() => alert("Erro ao carregar dados do cavalo."))
      .finally(() => setLoading(false))
  }, [isEditMode, id, tenantId])

  // Carrega raças
  useEffect(() => {
    api.get("/horse_breed").then(r => setBreeds(r.data.breed || r.data || []))
  }, [])

  // Rastreamento de alterações
  const isFieldModified = (field: string, newValue: any) =>
    originalData[field as keyof HorseFormData] !== newValue

  const updateModifiedFields = (field: string, newValue: any) => {
    setModifiedFields(prev => {
      const updated = new Set(prev)
      if (isFieldModified(field, newValue)) updated.add(field)
      else updated.delete(field)
      return updated
    })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    
    setHorseData(prev => ({ ...prev, [name]: value }))
    updateModifiedFields(name, value)
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
    
    if (noChangesWarning) {
      setNoChangesWarning(false)
    }
  }

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target
    
    if (name === "horseGender") {
      if (value === "M") {
        const newData = { ...horseData, horseGender: value }
        delete newData.horsePregnant
        delete newData.horseChildBirth
        delete newData.horseChildNumber
        setHorseData(newData)
      } else {
        setHorseData(prev => ({
          ...prev,
          horseGender: value,
          horsePregnant: false,
          horseChildBirth: "",
          horseChildNumber: "",
        }))
      }
      updateModifiedFields(name, value)
    } else {
      setHorseData(prev => ({ ...prev, [name]: value }))
      updateModifiedFields(name, value)
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
    
    if (noChangesWarning) {
      setNoChangesWarning(false)
    }
  }

  const handleSwitchChange = (name: string, checked: boolean) => {
    setHorseData(prev => ({ ...prev, [name]: checked }))
    updateModifiedFields(name, checked)
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
    
    if (noChangesWarning) {
      setNoChangesWarning(false)
    }
  }

  // Verifica se há alterações
  const hasChanges = (): boolean => {
    if (!isEditMode) return true // No modo criação, sempre permite salvar
    return modifiedFields.size > 0
  }

  // Gera dados para salvar
  const getModifiedData = (): any => {
    const payload: any = {}
    
    if (!isEditMode) {
      // Criação: enviar todos os campos
      payload.horseName = horseData.horseName
      payload.horseGender = horseData.horseGender
      payload.horseBreed = Number(horseData.horseBreed)
      payload.horseFather = horseData.horseFather || ""
      payload.horseMother = horseData.horseMother || ""
      payload.horseCastrated = horseData.horseCastrated
      
      if (horseData.horseBirth) payload.horseBirth = horseData.horseBirth
      
      if (horseData.horseGender === "F") {
        payload.horsePregnant = horseData.horsePregnant || false
        if (horseData.horsePregnant) {
          if (horseData.horseChildBirth) payload.horseChildBirth = horseData.horseChildBirth
          if (horseData.horseChildNumber) payload.horseChildNumber = Number(horseData.horseChildNumber)
        }
      }
    } else {
      // Edição: campos obrigatórios + todos os outros
      payload.horseId = horseData.horseId
      payload.horseName = horseData.horseName
      payload.horseGender = horseData.horseGender
      payload.horseBreed = Number(horseData.horseBreed)
      payload.horseFather = horseData.horseFather || ""
      payload.horseMother = horseData.horseMother || ""
      payload.horseCastrated = horseData.horseCastrated
      
      if (horseData.horseBirth) payload.horseBirth = horseData.horseBirth
      
      if (horseData.horseGender === "F") {
        payload.horsePregnant = horseData.horsePregnant || false
        if (horseData.horsePregnant) {
          if (horseData.horseChildBirth) payload.horseChildBirth = horseData.horseChildBirth
          if (horseData.horseChildNumber) payload.horseChildNumber = Number(horseData.horseChildNumber)
        } else {
          // Reverteu a prenhez: limpa também o nº de filhotes e a data prevista
          // de parto (mesmo comportamento do app: horsePregnant=0 + campos null).
          payload.horseChildBirth = null
          payload.horseChildNumber = null
        }
      }
    }

    return payload
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}
    
    if (!horseData.horseName?.trim()) newErrors.horseName = t("horses.nameRequired")
    if (!horseData.horseGender) newErrors.horseGender = t("horses.genderRequired")
    if (!horseData.horseBreed) newErrors.horseBreed = t("horses.breedRequired")

    // Data prevista de parto não pode ser no passado (só hoje ou futuro).
    if (horseData.horsePregnant && horseData.horseChildBirth) {
      const today = new Date().toISOString().split("T")[0]
      if (horseData.horseChildBirth < today) {
        newErrors.horseChildBirth = t("horses.birthDateMustBeFuture")
      }
    }


    // Validar se algum campo obrigatório original está sendo removido
    if (isEditMode) {
      // horseChildBirth/horseChildNumber ficam de fora: são campos reprodutivos
      // que PODEM ser removidos ao reverter a prenhez (ver abaixo).
      const fieldsToValidate = ['horseName', 'horseGender', 'horseBreed', 'horseBirth', 'horseFather', 'horseMother']
      
      fieldsToValidate.forEach(field => {
        const originalValue = originalData[field as keyof HorseFormData]
        const currentValue = horseData[field as keyof HorseFormData]
        
        if (originalValue && !currentValue) {
          newErrors[field] = t("horses.fieldCannotBeRemoved")
        }
      })
      
      // Apenas a castração é irreversível. A prenhez PODE ser revertida
      // (a égua pariu / perdeu a gestação), então não há trava aqui.
      if (originalData.horseCastrated === true && horseData.horseCastrated === false) {
        newErrors.horseCastrated = t("horses.castratedCannotBeReverted")
      }
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Salvar
  const handleSave = async () => {
    if (!validateForm()) return

    // Verificar se há alterações no modo edição
    if (isEditMode && !hasChanges()) {
      setNoChangesWarning(true)
      return
    }

    setSaving(true)
    try {
      const payload = getModifiedData()
      console.log("📤 Enviando:", payload)
      
      const url = isEditMode ? `/horse/${tenantId}/${id}` : `/horse/${tenantId}`
      const response = isEditMode ? await api.put(url, payload) : await api.post(url, payload)
      
      console.log("✅ Resposta:", response.data)
      navigateToPage("cavalos", { mode: "list", tenantId })
    } catch (err: any) {
      console.error("❌ Erro:", err)
      alert(err?.response?.data?.message || "Erro ao salvar cavalo.")
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => navigateToPage("cavalos", { mode: "list", tenantId })
  const getGridSize = () => (isMobile ? 12 : isTablet ? 6 : 6)

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        {isEditMode ? "Editar Cavalo" : "Novo Cavalo"}
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <HorseLoader />
        </Box>
      ) : (
        <Box component="form" noValidate>
          {noChangesWarning && (
            <Alert 
              severity="info" 
              sx={{ mb: 2 }} 
              onClose={() => setNoChangesWarning(false)}
            >
              Nenhuma alteração foi feita. Use o botão Cancelar para voltar à lista.
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={getGridSize()} md={6}>
              <TextField
                fullWidth
                label={t("fields.name")}
                name="horseName"
                value={horseData.horseName}
                onChange={handleInputChange}
                required
                error={!!errors.horseName}
                helperText={errors.horseName}
              />
            </Grid>

            <Grid item xs={getGridSize()} md={6}>
              <FormControl fullWidth required error={!!errors.horseGender}>
                <InputLabel>{t("fields.gender")}</InputLabel>
                <Select
                  name="horseGender"
                  value={horseData.horseGender}
                  onChange={handleSelectChange}
                  label={t("fields.gender")}
                >
                  <MenuItem value="M">{t("common.male")}</MenuItem>
                  <MenuItem value="F">{t("common.female")}</MenuItem>
                </Select>
                {errors.horseGender && <FormHelperText>{errors.horseGender}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={getGridSize()} md={6}>
              <TextField
                fullWidth
                label={t("fields.birthDate")}
                name="horseBirth"
                type="date"
                value={horseData.horseBirth || ""}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                error={!!errors.horseBirth}
                helperText={errors.horseBirth}
              />
            </Grid>

            <Grid item xs={getGridSize()} md={6}>
              <FormControl fullWidth required error={!!errors.horseBreed}>
                <InputLabel>{t("fields.breed")}</InputLabel>
                <Select
                  name="horseBreed"
                  value={horseData.horseBreed}
                  onChange={handleSelectChange}
                  label={t("fields.breed")}
                >
                  {breeds.map(breed => (
                    <MenuItem key={breed.horseBreedId} value={String(breed.horseBreedId)}>
                      {breed.horseBreedName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.horseBreed && <FormHelperText>{errors.horseBreed}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={getGridSize()} md={6}>
              <TextField
                fullWidth
                label={t("fields.father")}
                name="horseFather"
                value={horseData.horseFather}
                onChange={handleInputChange}
                error={!!errors.horseFather}
                helperText={errors.horseFather}
              />
            </Grid>

            <Grid item xs={getGridSize()} md={6}>
              <TextField
                fullWidth
                label={t("fields.mother")}
                name="horseMother"
                value={horseData.horseMother}
                onChange={handleInputChange}
                error={!!errors.horseMother}
                helperText={errors.horseMother}
              />
            </Grid>

            {horseData.horseGender === "M" && (
              <Grid item xs={getGridSize()} md={6}>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={horseData.horseCastrated}
                        onChange={(e) => handleSwitchChange("horseCastrated", e.target.checked)}
                      />
                    }
                    label={t("horses.castrated")}
                  />
                  {errors.horseCastrated && (
                    <FormHelperText error sx={{ ml: 0 }}>
                      {errors.horseCastrated}
                    </FormHelperText>
                  )}
                </Box>
              </Grid>
            )}

            {horseData.horseGender === "F" && (
              <>
                <Grid item xs={getGridSize()} md={6}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={horseData.horsePregnant || false}
                          onChange={(e) => handleSwitchChange("horsePregnant", e.target.checked)}
                        />
                      }
                      label={t("horses.pregnant")}
                    />
                    {errors.horsePregnant && (
                      <FormHelperText error sx={{ ml: 0 }}>
                        {errors.horsePregnant}
                      </FormHelperText>
                    )}
                  </Box>
                </Grid>

                {horseData.horsePregnant && (
                  <>
                    <Grid item xs={getGridSize()} md={6}>
                      <TextField
                        fullWidth
                        label={t("horses.expectedBirth")}
                        name="horseChildBirth"
                        type="date"
                        value={horseData.horseChildBirth || ""}
                        onChange={handleInputChange}
                        InputLabelProps={{ shrink: true }}
                        // Só hoje ou futuro (não permite data de parto no passado).
                        inputProps={{ min: new Date().toISOString().split("T")[0] }}
                        error={!!errors.horseChildBirth}
                        helperText={errors.horseChildBirth}
                      />
                    </Grid>

                    <Grid item xs={getGridSize()} md={6}>
                      <TextField
                        fullWidth
                        label={t("horses.childNumber")}
                        name="horseChildNumber"
                        type="number"
                        value={horseData.horseChildNumber || ""}
                        onChange={handleInputChange}
                        InputProps={{ inputProps: { min: 1 } }}
                        error={!!errors.horseChildNumber}
                        helperText={errors.horseChildNumber}
                      />
                    </Grid>

                    {/* Aviso: prenha sem data de parto → sem alertas de parto */}
                    {!horseData.horseChildBirth && (
                      <Grid item xs={12}>
                        <Alert severity="warning">
                          {t("horses.pregnantMissingBirthWarning")}
                        </Alert>
                      </Grid>
                    )}
                  </>
                )}
              </>
            )}
          </Grid>

          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" onClick={handleCancel} disabled={saving}>
              Cancelar
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSave} 
              disabled={saving || (isEditMode && !hasChanges())}
            >
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  )
}